import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button, Spinner, Typography } from "@material-tailwind/react";
import { useTranslation } from "react-i18next";
import { api_key, sdkURL } from "../utils/api-url-list";
import { faceScan } from "careplix-web-algo";
import Failure from "../assets/images/failure.gif";
import { ReactComponent as CheckFilled } from "../assets/icons/CheckFilled.svg";

const Scan = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation("Scan");

  const videoRef = useRef();
  const canvasRef = useRef();

  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingStep, setAnalyzingStep] = useState(0);
  const [error, setError] = useState("");
  const [scanFrameData, setScanFrameData] = useState({
    type: "",
    percentage: 0,
    timeElapsed: 0,
    isFaceInView: true,
    isLiteMode: false,
  });

  const analyzingMsgDuration = 1000;
  const analyzingMsgList = useMemo(
    () => [
      t("analyzing_message_0"),
      t("analyzing_message_1"),
      t("analyzing_message_2"),
      t("analyzing_message_3"),
      t("analyzing_message_4"),
      t("analyzing_message_5"),
    ],
    [t],
  );

  useEffect(() => {
    let timer;
    if (analyzing) {
      if (analyzingStep < analyzingMsgList.length - 1) {
        timer = setTimeout(() => {
          setAnalyzingStep((s) => s + 1);
        }, analyzingMsgDuration);
      }
    } else setAnalyzingStep(0);
    return () => {
      clearTimeout(timer);
    };
  }, [analyzing, analyzingMsgList.length, analyzingStep]);

  useEffect(() => {
    const controller = new AbortController();
    if (state.token?.length > 0) {
      const isPlatformIOS = /iPhone|iPad|iPod|Macintosh|Mac/i.test(
        navigator.userAgent,
      );
      faceScan.onFrame(setScanFrameData);
      faceScan.onScanFinish(({ raw_intensity, ppg_time, average_fps }) => {
        setAnalyzing(true);
        Promise.all([
          new Promise(async (resolve, reject) => {
            try {
              const resp = await fetch(sdkURL("/vitals/add-scan"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  api_key,
                  scan_token: state.token,
                  posture: "Resting",
                  dob: state.dob,
                  gender: state.gender,
                  metadata: {
                    physiological_scores: {
                      height: state.height,
                      weight: state.weight,
                    },
                    device: `RPPG_CAREPLIX_FACE_${
                      isPlatformIOS ? "IOS" : "ANDROID"
                    }`,
                    ppg_time: ppg_time,
                    raw_intensity: raw_intensity,
                    fps: average_fps,
                  },
                  notes: {
                    app_code: "CPVITAL",
                    day_code: state?.hcpCode,
                    access_code: state?.employeeCode,
                    state: state.state,
                    city: state.city,
                  },
                }),
                signal: controller.signal,
              });
              const resp_json = await resp.json();
              if (resp_json.statusCode?.toString().startsWith("2"))
                resolve(resp_json);
              else throw new Error(resp_json.message);
            } catch (err) {
              reject(err);
            }
          }),
          new Promise((resolve) => {
            setTimeout(resolve, analyzingMsgList.length * analyzingMsgDuration);
          }),
        ])
          .then(([result]) => {
            navigate("/result", { state: { ...state, result }, replace: true });
          })
          .catch((err) => {
            console.error(err);
            setError(err.message);
          });
      });
      faceScan.onError((err) => {
        console.error(err);
        setError(err.message);
      });
      faceScan
        .startScan({
          videoElement: videoRef.current,
          canvasElement: canvasRef.current,
          calibrationTime_inSec: 5,
          scanTime_inSec: 60,
          strict_mode: false,
          draw_type: "corner-box",
          draw_color: "#fff",
        })
        .then(() => {
          console.log("Scan Started");
        })
        .catch((err) => {
          console.error(err);
        });
    } else setError("Invalid Scan Token");
    return () => {
      faceScan.stopScan(true);
      controller.abort();
    };
  }, [navigate, state, analyzingMsgList.length]);

  return (
    <section className="relative h-screen w-screen">
      <video
        ref={videoRef}
        id="videoInput"
        className="fixed top-4 right-4 h-px w-px"
        autoPlay
        muted
        playsInline
      />

      <canvas
        ref={canvasRef}
        id="canvasOutput"
        className="w-full h-full -scale-x-100"
      />

      {scanFrameData.type === "scan" && (
        <div
          className="scan-animation"
          style={{ "--tw-gradient-from": "rgb(21 69 214 / 0.75)" }}
        />
      )}

      {!scanFrameData.isFaceInView && (
        <p className="fixed top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl bg-white text-primary text-sm sm:text-xl text-center font-medium whitespace-nowrap">
          {t("noFace")}
        </p>
      )}

      {faceScan.isInitializing() && (
        <div className="fixed top-0 bottom-0 left-0 right-0  rounded-2xl bg-[#fcfcfc] px-8 py-12 flex flex-col items-center justify-center text-center">
          <Spinner
            color="indigo"
            className="h-8 w-8 sm:h-12 sm:w-12 text-primary/20"
          />
          <Typography
            variant="h6"
            className="mt-4 text-xs sm:text-base text-primary font-medium"
          >
            {t("initializing")}
          </Typography>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-white">
        <div className="relative h-2.5 overflow-hidden bg-stroke/60">
          <div
            className="absolute left-0 top-0 bottom-0 bg-primary transition-all duration-1000"
            style={{
              width:
                scanFrameData.type === "scan"
                  ? `${scanFrameData.percentage}%`
                  : 0,
            }}
          />
        </div>

        <div className="m-6">
          <h3 className="text-[10px] sm:text-xs font-medium">Disclaimer :</h3>
          <p className="mt-0.5 sm:mt-1 text-justify text-[8px] sm:text-[10px] leading-tight">
            For Investigational Use Only. CarePlix is not a substitute for the
            clinical judgment of a healthcare professional. CarePlix is intended
            to improve your awareness of general wellness. CarePlix does not
            diagnose, treat, mitigate or prevent any disease, symptom, disorder
            or abnormal physical state. Consult with a healthcare professional
            or emergency services if you believe you may have a medical issue.
          </p>
        </div>

        <Button
          size="sm"
          color="white"
          className="block ml-auto mr-6 mb-4 bg-primary text-white border border-primary text-xs sm:text-sm font-medium normal-case"
          onClick={() => navigate(-1)}
        >
          {t("Cancel")}
        </Button>
      </div>

      {error.length > 0 ? (
        <div className="fixed z-20 top-0 bottom-0 left-0 right-0 bg-[#fcfcfc] px-8 py-12 flex flex-col items-center justify-center text-center">
          <img src={Failure} alt="failure icon" className="w-1/3 mx-auto" />
          <Typography
            variant="lead"
            className="mt-2.5 sm:mt-4 text-xs sm:text-base text-primary whitespace-pre-line"
          >
            {"Scan Failed!\nPlease try again."}
          </Typography>
          <Button
            color="white"
            className="mt-6 sm:mt-8 bg-white text-primary/80 border border-primary text-sm sm:text-lg normal-case"
            onClick={() => navigate(-1)}
          >
            {t("Back")}
          </Button>
        </div>
      ) : (
        analyzing && (
          <div className="fixed z-20 top-0 bottom-0 left-0 right-0 bg-[#fcfcfc] p-[15%] flex flex-col items-stretch justify-evenly space-y-8">
            {analyzingMsgList.map((msg, index) => (
              <div
                key={index}
                className="flex items-center justify-between space-x-3 sm:space-x-6"
              >
                <Typography
                  variant="h4"
                  className="text-primary text-sm sm:text-2xl tracking-widest"
                >
                  {msg}
                </Typography>
                {analyzingStep === index ? (
                  <Spinner
                    color="indigo"
                    className="shrink-0 h-6 w-6 sm:h-12 sm:w-12 text-primary/20"
                  />
                ) : (
                  analyzingStep > index && (
                    <CheckFilled className="shrink-0 h-6 w-6 sm:h-12 sm:w-12 text-primary" />
                  )
                )}
              </div>
            ))}
          </div>
        )
      )}
    </section>
  );
};

export default Scan;
